import { createContext, useContext, type ReactNode } from 'react';
import type { LessonTranscript, TranscriptResponse, TranscriptStep } from '../lib/lessonTranscript.js';

// Null preserves the standalone transcript's existing presentation.
export const QueueTranscriptContext = createContext<{ continuation?: number | undefined; transcript: LessonTranscript } | null>(null);

export function stepName(id: number, transcript: LessonTranscript | null): string {
  const step = transcript?.steps.find((candidate) => candidate.stepId === id);
  if (!step) return `the step with id ${id} (position and prompt unavailable)`;
  const words = step.displayText.trim().split(/\s+/);
  return `the step at position ${step.position} (id ${id}), "${words.slice(0, 8).join(' ')}${words.length > 8 ? '…' : ''}"`;
}

function TechnicalDetails({ children }: { children: ReactNode }) {
  const queue = useContext(QueueTranscriptContext);
  return queue ? <details className="mt-2 text-xs text-slate-600"><summary className="cursor-pointer font-semibold">Technical details</summary>{children}</details> : <>{children}</>;
}

export function RawStoredField({ label, raw }: { label: string; raw: string | null }) {
  if (raw === null) return null;
  return (
    <TechnicalDetails><details className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
      <summary className="cursor-pointer font-semibold text-slate-600">Raw stored HTML — {label}</summary>
      <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-5 text-slate-700">{raw}</pre>
    </details></TechnicalDetails>
  );
}

export function WarningList({ warnings }: { warnings: string[] }) {
  const queue = useContext(QueueTranscriptContext);
  const visibleWarnings = queue?.continuation === undefined ? warnings : warnings.filter((warning) =>
    !warning.endsWith(`Route points to step ${queue.continuation}, which is not in the document.`));
  if (visibleWarnings.length === 0) return null;
  return (
    <ul className="mt-3 space-y-1 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
      {visibleWarnings.map((warning, index) => <li key={`${warning}-${index}`}>Warning: {warning}</li>)}
    </ul>
  );
}

function answerKeyLabel(response: TranscriptResponse): string {
  if (response.isCorrectInvalid) return 'Invalid stored value';
  if (response.isCorrect === true) return 'Keyed correct';
  if (response.isCorrect === false) return 'Not keyed correct';
  return 'No answer key set';
}

function responseRole(response: TranscriptResponse): string {
  if (response.responseType === 'catchall') {
    return 'Branch taken by input matching no option — not a clickable answer';
  }
  if (response.isFallback) {
    return 'Step-level fallback branch — not one of the response-group options';
  }
  if (response.source === 'group') return 'Learner-visible option from a response group';
  return 'Step-level response option';
}

export function ResponseBlock({ response, orphan = false, isNew = false, reroutedTo }: {
  response: TranscriptResponse;
  orphan?: boolean;
  isNew?: boolean;
  reroutedTo?: string | undefined;
}) {
  const queue = useContext(QueueTranscriptContext);
  const Heading = queue ? 'p' : 'h4';
  return (
    <li
      className={`rounded-xl border border-slate-200 bg-white p-4 shadow-sm${isNew ? ' border-l-4 border-l-emerald-500' : ''}`}
      data-testid={orphan ? `orphan-response-${response.responseId}` : `transcript-response-${response.responseId}`}
    >
      <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {queue ? (
          <TechnicalDetails><span>Response id {response.responseId}</span>{response.responseType ? <span> · Stored type: {response.responseType}</span> : null}</TechnicalDetails>
        ) : <span>Response id {response.responseId}</span>}
        {isNew ? <NewBadge /> : null}
        <span className="rounded-full bg-indigo-50 px-2 py-1 text-indigo-800">{answerKeyLabel(response)}</span>
        {!queue && response.responseType ? <span>Stored type: {response.responseType}</span> : null}
      </div>
      <p className="mt-2 text-xs font-semibold text-slate-600">{orphan ? 'Orphan response — no step references this record' : responseRole(response)}</p>
      <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-slate-950">
        {response.text || '(Stored option text is empty.)'}
      </p>
      <RawStoredField label="option text" raw={response.rawText} />

      {response.altValues.length > 0 ? (
        <div className="mt-3">
          <Heading className="text-xs font-bold uppercase tracking-wide text-slate-500">Alternative accepted values</Heading>
          <ul className="mt-1 space-y-2">
            {response.altValues.map((value, index) => (
              <li className="rounded-lg bg-slate-50 p-2 text-sm" key={`${value.raw}-${index}`}>
                {value.text || '(Stored alternative value is empty.)'}
                <RawStoredField label={`alternative value ${index + 1}`} raw={value.raw} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {response.rawFeedback !== null ? (
        <div className="mt-4 border-l-4 border-cyan-300 pl-3">
          <Heading className="text-xs font-bold uppercase tracking-wide text-slate-500">Inline feedback</Heading>
          <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-6 text-slate-800">
            {response.feedback || '(Stored feedback has no plain text.)'}
          </p>
          <RawStoredField label="inline feedback" raw={response.rawFeedback} />
        </div>
      ) : null}

      {response.rawTargetStepId !== undefined && response.rawTargetStepId !== null ? (
        <div className="mt-4 border-l-4 border-violet-300 pl-3">
          <Heading className="text-xs font-bold uppercase tracking-wide text-slate-500">Routed branch</Heading>
          <p className="mt-1 text-sm font-semibold text-slate-800">
            {queue ? `Leads to ${response.targetStepId === null ? 'an invalid stored destination' : stepName(response.targetStepId, queue.transcript)}` : `Routed step id: ${response.targetStepId ?? 'invalid stored value'}`}
          </p>
          {response.targetStepMissing && response.targetStepId === queue?.continuation ? (
            <p className="mt-1 text-sm">Returns to the existing lesson at {stepName(response.targetStepId!, queue.transcript)}.</p>
          ) : response.targetStepAmbiguous ? (
            <p className="mt-1 text-sm text-amber-900">Destination text is not asserted because more than one stored step has this id.</p>
          ) : response.targetStepMissing ? (
            <p className="mt-1 text-sm text-amber-900">That step is not present in this document.</p>
          ) : (
            <>
              <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-6 text-slate-800">
                {response.targetStepText || '(The routed step has no plain text.)'}
              </p>
              <RawStoredField label="routed step text" raw={response.rawTargetStepText} />
            </>
          )}
        </div>
      ) : null}
      {reroutedTo !== undefined ? <p className="mt-2 font-semibold text-violet-800">Rerouted: now leads to {reroutedTo}</p> : null}
      <WarningList warnings={response.warnings} />
    </li>
  );
}

function reachabilityLabel(step: TranscriptStep): string {
  switch (step.reachability) {
    case 'reachable': return 'Reachable from the declared root';
    case 'unreachable': return 'Unreachable from the declared root';
    case 'in_cycle': return 'Member of a stored step cycle';
    case 'unknown': return 'Reachability unknown';
  }
}

export function StepBlock({ step, isNew = false, newResponseIds = [], reroutedResponses = new Map(), reroutedTo, testId }: {
  step: TranscriptStep;
  isNew?: boolean;
  newResponseIds?: readonly number[];
  reroutedResponses?: ReadonlyMap<number, string>;
  reroutedTo?: string | undefined;
  testId?: string;
}) {
  const queue = useContext(QueueTranscriptContext);
  const StepHeading = queue ? 'h5' : 'h2';
  const SectionHeading = queue ? 'h6' : 'h3';
  return (
    <article
      className={`rounded-2xl border border-slate-200 bg-slate-50 p-5 shadow-sm sm:p-6${isNew ? ' border-l-4 border-l-emerald-500' : ''}`}
      data-testid={testId ?? 'transcript-step'}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          {isNew ? <NewBadge /> : null}
          {!queue ? <p className="text-xs font-bold uppercase tracking-[0.16em] text-cyan-700">Stored position {step.position}</p> : null}
          <StepHeading className="font-serif text-2xl font-semibold text-slate-950">Step {step.position} {!queue ? <span className="font-sans text-base font-normal text-slate-500">· {step.stepId < 0 ? 'stored id missing or invalid' : `stored id ${step.stepId}`}</span> : null}</StepHeading>
          {queue ? <TechnicalDetails>Stored position {step.position} · stored id {step.stepId}</TechnicalDetails> : null}
        </div>
        <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600">{reachabilityLabel(step)}</span>
      </div>

      <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4">
        <SectionHeading className="text-xs font-bold uppercase tracking-wide text-slate-500">Stored prompt</SectionHeading>
        <p className="mt-2 whitespace-pre-wrap break-words text-base leading-7 text-slate-950">
          {step.prompt || '(Stored prompt is empty.)'}
        </p>
        <RawStoredField label="prompt" raw={step.rawPrompt} />
        {step.rawValue !== null ? (
          <div className="mt-4 border-t border-slate-100 pt-4">
            <SectionHeading className="text-xs font-bold uppercase tracking-wide text-slate-500">Stored value</SectionHeading>
            <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-slate-800">
              {step.value || '(Stored value has no plain text.)'}
            </p>
            {step.prompt === '' && step.value ? (
              <p className="mt-2 text-xs font-semibold text-cyan-800">Used as the display-text fallback because the stored prompt renders empty.</p>
            ) : null}
            <RawStoredField label="value" raw={step.rawValue} />
          </div>
        ) : null}
        {step.displayText.includes('[[summary]]') ? (
          <p className="mt-3 rounded-lg border border-violet-200 bg-violet-50 p-3 text-sm text-violet-950">
            [[summary]] is stored verbatim. The vendored player expands this placeholder; this page does not.
          </p>
        ) : null}
      </div>

      <TechnicalDetails><dl className="mt-4 grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
        <div><dt className="font-semibold">Document marks as question</dt><dd>{step.storedIsQuestionInvalid ? 'Invalid stored value' : step.storedIsQuestion === true ? 'Yes' : step.storedIsQuestion === false ? 'No' : 'Not stated'}</dd></div>
        <div><dt className="font-semibold">Has wired responses</dt><dd>{step.hasWiredResponses ? 'Yes' : 'No'}</dd></div>
        <div><dt className="font-semibold">Root marker</dt><dd>{step.isRoot ? 'Yes' : 'No'}</dd></div>
        <div><dt className="font-semibold">Try-again marker</dt><dd>{step.isTryAgain ? 'Yes' : 'No'}</dd></div>
        <div><dt className="font-semibold">Response format</dt><dd>{step.responseFormat}</dd></div>
      </dl></TechnicalDetails>

      {step.videoUrl || step.rawVideoText !== null ? (
        <section className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
          <SectionHeading className="text-xs font-bold uppercase tracking-wide text-slate-500">Step media</SectionHeading>
          {step.videoUrl ? <p className="mt-2 break-all text-sm text-slate-800">Video URL: {step.videoUrl}</p> : null}
          {step.rawVideoText !== null ? (
            <>
              <p className="mt-2 whitespace-pre-wrap break-words text-sm text-slate-800">Video text: {step.videoText || '(No plain text.)'}</p>
              <RawStoredField label="video text" raw={step.rawVideoText} />
            </>
          ) : null}
        </section>
      ) : null}

      {step.rawNextStepId !== undefined && step.rawNextStepId !== null ? (
        <section className="mt-4 rounded-xl border border-violet-200 bg-violet-50 p-4">
          <SectionHeading className="text-xs font-bold uppercase tracking-wide text-violet-700">Step-level route</SectionHeading>
          <p className="mt-1 text-sm font-semibold text-violet-950">{queue ? `Leads to ${step.nextStepId === null ? 'an invalid stored destination' : stepName(step.nextStepId, queue.transcript)}` : `Next step id: ${step.nextStepId ?? 'invalid stored value'}`}</p>
          {step.nextStepMissing && step.nextStepId === queue?.continuation ? (
            <p className="mt-1 text-sm">Returns to the existing lesson at {stepName(step.nextStepId!, queue.transcript)}.</p>
          ) : step.nextStepAmbiguous ? (
            <p className="mt-1 text-sm text-violet-950">Destination text is not asserted because more than one step carries that id.</p>
          ) : step.nextStepMissing ? (
            <p className="mt-1 text-sm text-violet-950">That step is not present in this document.</p>
          ) : (
            <>
              <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-6 text-violet-950">{step.nextStepText || '(The routed step has no plain text.)'}</p>
              <RawStoredField label="next step text" raw={step.rawNextStepText} />
            </>
          )}
        </section>
      ) : null}

      {step.responses.length > 0 ? (
        <section className="mt-5">
          <SectionHeading className="text-sm font-bold uppercase tracking-wide text-slate-700">Stored responses ({step.responses.length})</SectionHeading>
          <ol className="mt-3 space-y-3">{step.responses.map((response, index) => <ResponseBlock key={`${response.responseId}-${index}`} response={response} isNew={newResponseIds.includes(response.responseId)} reroutedTo={reroutedResponses.get(response.responseId)} />)}</ol>
        </section>
      ) : (
        <p className="mt-4 text-sm text-slate-500">No responses are wired to this step.</p>
      )}
      {reroutedTo !== undefined ? <p className="mt-2 font-semibold text-violet-800">Rerouted: now leads to {reroutedTo}</p> : null}
      <WarningList warnings={step.warnings} />
    </article>
  );
}

function NewBadge() {
  return <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-bold text-emerald-800">New</span>;
}
